import { useQuery } from "@tanstack/react-query";
import { DetailField, DetailList } from "../../../../../../../shared/frontend/components/DetailList";
import { getPaymentMethodLabel } from "../../../../../../../shared/frontend/billing/paymentMethodDisplay";
import type { useAuth } from "../../../app/auth/useAuth";
import { authQueryScope } from "../../../app/query/authQueryScope";
import { formatDateOnly } from "../../../app/units/format";
import { useEffectiveUnits } from "../../../app/units/useEffectiveUnits";
import type { I18nContextValue } from "../../../app/i18n/useI18n";

/** Wirkung eines Profilwechsels je Baustein, aus derselben Regel wie die spätere Umstellung (`/payment-profile/impact`). */
export interface PersonContractPaymentImpact {
  contractId: string;
  name: string;
  current: string | null;
  after: string | null;
  currentName: string | null;
  afterName: string | null;
  follows: boolean;
  from: string;
}

export type PaymentProfileTarget = { local: number } | { method: string; interfaceId?: string };

export function PersonPaymentProfileImpact({ auth, personId, target, text }: {
  auth: ReturnType<typeof useAuth>; personId: string; target: PaymentProfileTarget | null; text: I18nContextValue["text"];
}) {
  const units = useEffectiveUnits();
  const query = new URLSearchParams(target == null ? {} : "local" in target ? { local: String(target.local) }
    : { method: target.method, ...(target.interfaceId ? { interfaceId: target.interfaceId } : {}) }).toString();
  const impact = useQuery({
    queryKey: ["person-payment-profile-impact", ...authQueryScope(auth.api, auth.info), personId, query],
    queryFn: () => auth.api.get<PersonContractPaymentImpact[]>(
      `/api/v1/persons/${encodeURIComponent(personId)}/payment-profile/impact?${query}`),
    enabled: target != null,
  });
  if (target == null || !impact.data?.length) return null;
  const label = (method: string | null, name: string | null) => name
    ?? (method ? getPaymentMethodLabel(method, { native_sepa: text("Direct debit"), card: text("Credit card") }) : text("Not set"));
  return <DetailList>
    {impact.data.map(row => <DetailField key={row.contractId} label={row.name}>
      {row.follows && row.current !== row.after
        ? text("{0} → {1} from {2}").replace("{0}", label(row.current, row.currentName)).replace("{1}", label(row.after, row.afterName))
          .replace("{2}", formatDateOnly(row.from, units))
        : text("remains {0}").replace("{0}", label(row.current, row.currentName))}
    </DetailField>)}
  </DetailList>;
}
